import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Skeleton,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Translate as TranslateIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  StickyNote2 as NoteIcon,
  StickyNote2Outlined as NoteOutlinedIcon,
} from '@mui/icons-material';
import type { TranslationFetchState } from '@/hooks/useYouTubeTranslations';
import type { useYouTubeTranslationEdit } from '@/hooks/useYouTubeTranslationEdit';
import { ROUTES } from '@/config/constants';
import {
  studyEditorResponsiveLayoutSx,
  studyEditorResponsiveColumnSx,
} from './videoPlayerResponsiveLayout';
import { NoteModal } from '@/components/notes/NoteModal';

interface VideoPlayerTranslationPanelProps {
  /** The currently selected segment, or null if none is active. */
  segment: {
    id: number;
    start_time: number;
    end_time: number;
    hasTranslationNote?: boolean;
  } | null;
  /** 1-based display index of the segment within the video's segment list. */
  segmentIndex: number | null;
  /**
   * Current translation fetch lifecycle state from useYouTubeTranslations.
   * Defaults to { status: 'idle' } when not provided.
   */
  translationState?: TranslationFetchState;
  /**
   * Called when the user clicks "Get Translation".
   * Translation fetch is strictly user-initiated — never auto-called on render
   * or segment change (mirrors Story 3.1/3.2 philosophy).
   */
  onGetTranslation?: () => void;
  /**
   * Translation edit state hook instance, created in VideoPlayer and passed down.
   * Follows the same prop-drilling pattern as captionState / editHook.
   * Do NOT create the hook inside this panel.
   */
  editHook?: ReturnType<typeof useYouTubeTranslationEdit>;
  /**
   * Called when the user clicks "Go to Transcript tab" from the no-transcript state.
   * Allows the translation panel to navigate to the transcript tab without owning
   * the tab state — VideoPlayer manages active tab state.
   */
  onSwitchToTranscript?: () => void;
  /**
   * True while the playhead is between segments during free playback.
   * The panel renders an animated skeleton placeholder instead of the
   * "select a segment" hint, keeping the layout height stable.
   */
  isGap?: boolean;
}

/**
 * VideoPlayerTranslationPanel — translation fetch + user editing surface.
 *
 * Story 3.3 scope: yt-dlp translation tracks + Google Cloud Translate fallback,
 * user editable translations, and source-aware rendering.
 *
 * Display priority (matches backend rule):
 *   1. source='user'          → enter edit mode immediately; pre-seed with user text.
 *   2. source='google_translate' → enter edit mode immediately (EMPTY seed); the google
 *      text renders in the auto-translation comparison column, never in the textarea.
 *   3. source='youtube'       → render as read-only; show "Edit" button (empty seed).
 *   4. no-transcript state    → show guidance + "Go to Transcript tab" button.
 *   5. no-translation / idle  → show "Get Translation" call-to-action.
 *
 * Key difference from transcript panel:
 * - source='youtube' is read-only; source='google_translate' opens the edit layout
 *   with the auto text shown as a side-by-side reference.
 * - no-transcript has its own state with navigation guidance.
 * - No font-size controls (translations are secondary study aids, simpler UI).
 */
export const VideoPlayerTranslationPanel: FC<VideoPlayerTranslationPanelProps> = ({
  segment,
  translationState = { status: 'idle' },
  onGetTranslation,
  editHook,
  onSwitchToTranscript,
  isGap = false,
}) => {
  const { t } = useTranslation('player');
  const { t: tSegments } = useTranslation('segments');
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  const isEditing = editHook?.editMode ?? false;

  // Current translation text shown to the user, used to prepopulate a new note.
  // Prefer the live edit buffer while editing; otherwise fall back to the
  // currently displayed (successfully fetched) translation text.
  const noteSourceText = isEditing
    ? (editHook?.editText ?? '')
    : translationState.status === 'success'
      ? translationState.translation
      : '';

  const [refreshSnapshot, setRefreshSnapshot] = useState<TranslationFetchState | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Tracks whether the effect below has actually observed translationState
  // transition to 'loading' since the current refresh started. Clicking
  // refresh sets isRefreshing=true synchronously, but on that same render
  // translationState is still whatever it was pre-click (often 'success') —
  // the parent hasn't re-rendered with 'loading' yet. Without this flag the
  // clear-on-resolve effect below would see isRefreshing===true &&
  // status !== 'loading' on that very render and immediately clear the
  // refresh state, discarding the snapshot before the parent ever reaches
  // 'loading'. Only once 'loading' has actually been observed does a later
  // non-loading status count as "the fetch resolved."
  const [sawRefreshLoading, setSawRefreshLoading] = useState(false);

  useEffect(() => {
    if (!segment || !editHook || isEditing || translationState.status !== 'success') return;
    // source='user': pre-seed the textarea with the user's own saved translation.
    // source='google_translate': open the edit layout with an EMPTY textarea — the
    // google text belongs in the auto-translation comparison column, never in the
    // manual textarea. Only source='youtube' stays read-only with an Edit button.
    if (translationState.source === 'user') {
      editHook.enterEditMode(segment.id, translationState.translation);
    } else if (translationState.source === 'google_translate') {
      editHook.enterEditMode(segment.id, '');
    }
  }, [editHook, isEditing, segment, translationState]);

  // Always-editable: when the segment has no saved translation (idle — nothing
  // cached — or no-translation), auto-open an empty edit textarea instead of the
  // placeholder + buttons, mirroring the podcast editor and the transcript panel.
  // Never triggers a fetch — "Get auto translation" stays available in the edit
  // layout's comparison column.
  useEffect(() => {
    if (!segment || isGap || !editHook || isEditing) return;
    if (translationState.status === 'idle' || translationState.status === 'no-translation') {
      editHook.enterEditMode(segment.id, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment?.id, isGap, isEditing, translationState.status]);

  // Discard any stale refresh snapshot when the selected segment changes so a
  // refresh started on a previous segment can't bleed into the new one.
  useEffect(() => {
    setRefreshSnapshot(null);
    setIsRefreshing(false);
    setSawRefreshLoading(false);
  }, [segment?.id]);

  // Clear the refresh snapshot once the triggered fetch resolves (success,
  // error, no-translation, etc.) so the panel falls back to rendering
  // translationState normally instead of the stale pre-refresh snapshot.
  // See the sawRefreshLoading comment above for why the loading transition
  // must be observed before a non-loading status is treated as "resolved."
  useEffect(() => {
    if (!isRefreshing) return;
    if (translationState.status === 'loading') {
      setSawRefreshLoading(true);
      return;
    }
    if (sawRefreshLoading) {
      setIsRefreshing(false);
      setRefreshSnapshot(null);
      setSawRefreshLoading(false);
    }
  }, [isRefreshing, sawRefreshLoading, translationState.status]);

  /**
   * Edit click handler for the read-only source ('youtube').
   * Empty seed — user writes from scratch; the YouTube text shows as reference.
   */
  const handleEditClick = () => {
    if (!segment || translationState.status !== 'success') return;
    editHook?.enterEditMode(segment.id, '');
  };

  const handleRefreshClick = () => {
    if (translationState.status !== 'success' || !onGetTranslation) return;
    setRefreshSnapshot(translationState);
    setIsRefreshing(true);
    onGetTranslation();
  };

  /** Write-from-scratch handler for idle / no-translation / error states. */
  const handleWriteFromScratch = () => {
    if (!segment) return;
    editHook?.enterEditMode(segment.id, '');
  };

  const renderSaveIndicator = () => {
    if (!editHook) return null;
    if (editHook.saveState === 'saving') {
      return (
        <CircularProgress
          size={14}
          sx={{ ml: 0.75 }}
          aria-hidden="true"
          data-testid="translation-save-spinner"
        />
      );
    }
    if (editHook.saveState === 'saved') {
      return (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, ml: 0.75, color: 'success.main' }}
          aria-hidden="true"
          data-testid="translation-save-check"
        />
      );
    }
    return null;
  };

  const renderEditArea = () => {
    // Show the auto-translation as a read-only reference when present.
    // - source='user': translationState.youtubeTranslation contains the auto cache.
    // - source='youtube' | 'google_translate': the fetched text IS the auto
    //   translation; show it in the comparison column next to the textarea.
    const youtubeComparison =
      translationState.status === 'success'
        ? translationState.source === 'user'
          ? translationState.youtubeTranslation
          : translationState.translation
        : undefined;

    return (
      <Stack spacing={1.5}>
        <Box sx={studyEditorResponsiveLayoutSx} data-testid="translation-edit-layout">
          <Stack sx={studyEditorResponsiveColumnSx} spacing={1}>
            <Stack direction="row" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t('translationEditLabel')}
              </Typography>
              {renderSaveIndicator()}
              {editHook?.saveState === 'error' && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ ml: 0.75 }}
                  data-testid="translation-save-error"
                >
                  ✕
                </Typography>
              )}
            </Stack>
            <TextField
              multiline
              minRows={3}
              fullWidth
              value={editHook?.editText ?? ''}
              onChange={(e) => editHook?.onTextChange(e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ 'aria-label': t('translationEditLabel'), style: { lineHeight: 1.7 } }}
              data-testid="translation-textarea"
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={() => editHook?.saveNow()}
                disabled={!editHook?.hasUnsavedChanges || editHook?.saveState === 'saving'}
                data-testid="translation-save-btn"
                sx={{ minHeight: 44 }}
              >
                {t('saveTranslation')}
              </Button>
            </Stack>
          </Stack>
          <Stack sx={studyEditorResponsiveColumnSx} spacing={0.5}>
            {youtubeComparison ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t('autoTranslationVersionLabel')}
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    borderLeft: 3,
                    borderColor: 'info.main',
                    maxHeight: 160,
                    overflowY: 'auto',
                  }}
                  data-testid="translation-youtube-comparison"
                >
                  <span
                    data-testid="translation-youtube-comparison-text"
                    style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, display: 'block' }}
                  >
                    {youtubeComparison}
                  </span>
                </Box>
              </>
            ) : translationState.status === 'loading' ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={14} aria-hidden="true" data-testid="translation-auto-loading" />
                <Typography variant="caption" color="text.secondary">
                  {t('translationLoading')}
                </Typography>
              </Stack>
            ) : onGetTranslation ? (
              <Button
                variant="text"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={onGetTranslation}
                data-testid="translation-compare-btn"
                sx={{ color: 'text.secondary', alignSelf: 'flex-start', pl: 0, minHeight: 44 }}
              >
                {t('getAutoTranslation')}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    );
  };

  return (
    <Box data-testid="translation-panel" sx={{ borderTop: 1, borderColor: 'divider' }}>
      {segment && (
        <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pt: 1 }}>
          <Tooltip title={segment.hasTranslationNote ? tSegments('noteViewEditTooltip') : tSegments('noteAddTooltip')}>
            <IconButton
              size="small"
              onClick={() => setIsNoteModalOpen(true)}
              color={segment.hasTranslationNote ? 'primary' : 'default'}
              data-testid="translation-note-icon"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              {segment.hasTranslationNote ? <NoteIcon fontSize="small" /> : <NoteOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {/* Content area — minHeight keeps the panel stable while translation loads */}
      <Box sx={{ px: 2, py: 2, minHeight: 168, bgcolor: 'grey.50' }}>
        {isGap || segment == null ? (
          isGap ? (
            /* ── Inter-segment gap: skeleton placeholder ────────────────── */
            <Paper
              variant="outlined"
              sx={{ p: 1.5, bgcolor: 'white', minHeight: 120, borderRadius: 1 }}
              data-testid="translation-gap-skeleton"
            >
              <Skeleton variant="text" sx={{ fontSize: '16px', width: '88%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: '16px', width: '72%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: '16px', width: '80%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: '16px', width: '55%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: '16px', width: '68%' }} />
            </Paper>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {t('selectSegmentFirst')}
            </Typography>
          )
        ) : isEditing ? (
          /* ── Edit mode ───────────────────────────────────────────────── */
          <Stack spacing={1.5} data-testid="translation-content" sx={{ position: 'relative' }}>
            <Box
              component="span"
              aria-live="polite"
              aria-atomic="true"
              data-testid="translation-aria-live"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {editHook?.saveState === 'saving'
                ? t('translationSaving')
                : editHook?.saveState === 'saved'
                  ? t('translationSaved')
                  : ''}
            </Box>
            {renderEditArea()}
          </Stack>
        ) : (translationState.status === 'success' || (isRefreshing && refreshSnapshot?.status === 'success')) ? (
          /* ── Success: source-aware rendering ─────────────────────────── */
          (() => {
            const displayState =
              isRefreshing && refreshSnapshot?.status === 'success' ? refreshSnapshot : translationState;
            if (displayState.status !== 'success') return null;
            return (
          <Stack spacing={1.5} data-testid="translation-content" sx={{ position: 'relative' }}>
            <Box
              component="span"
              aria-live="polite"
              aria-atomic="true"
              data-testid="translation-aria-live"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {editHook?.saveState === 'saving'
                ? t('translationSaving')
                : editHook?.saveState === 'saved'
                  ? t('translationSaved')
                  : ''}
            </Box>

            {displayState.source === 'user' ||
            displayState.source === 'google_translate' ? (
              /* source='user': edit mode entered via effect, pre-seeded with saved text.
                 source='google_translate': edit mode entered via effect with an empty
                 textarea; the google text renders in the comparison column. */
              renderEditArea()
            ) : (
              /* source='youtube': read-only + Edit button (empty seed) + Refresh */
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid="translation-readonly-hint"
                  >
                    {t('translationSourceYoutube')}
                  </Typography>
                  {!isRefreshing ? (
                    <Tooltip title={t('refreshTranslation')}>
                      <IconButton
                        size="small"
                        onClick={handleRefreshClick}
                        data-testid="translation-refresh-btn"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('refreshingTranslation')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled
                          data-testid="translation-refresh-btn"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <CircularProgress size={16} data-testid="translation-refresh-spinner" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                <span
                  data-testid="translation-text"
                  style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, display: 'block' }}
                >
                  {displayState.translation}
                </span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                  data-testid="translation-edit-btn"
                  sx={{ alignSelf: 'flex-start', minHeight: 44 }}
                >
                  {t('editTranslation')}
                </Button>
              </Stack>
            )}
          </Stack>
            );
          })()
        ) : translationState.status === 'loading' ? (
          /* ── Loading ─────────────────────────────────────────────────────── */
          <Stack direction="row" alignItems="center" spacing={1.5} data-testid="translation-loading">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('translationLoading')}
            </Typography>
          </Stack>
        ) : translationState.status === 'no-transcript' ? (
          /* ── No transcript: guide user to Transcript tab first ───────── */
          <Stack spacing={1.5} alignItems="flex-start" data-testid="translation-no-transcript">
            <Alert severity="info" sx={{ py: 0.5, width: '100%' }}>
              {t('translationNoTranscript')}
            </Alert>
            {onSwitchToTranscript && (
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={onSwitchToTranscript}
                data-testid="translation-go-transcript-btn"
                sx={{ minHeight: 44 }}
              >
                {t('translationNoTranscriptAction')}
              </Button>
            )}
          </Stack>
        ) : translationState.status === 'api-unavailable' ? (
          /* ── YouTube/translation API temporarily unavailable — NOT a subscription issue ── */
          <Stack spacing={1.5} alignItems="flex-start" data-testid="translation-api-unavailable">
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('translationAPIUnavailable')}
            </Alert>
            {onGetTranslation && (
              <Button
                variant="outlined"
                size="small"
                onClick={onGetTranslation}
                data-testid="translation-retry-btn"
                sx={{ minHeight: 44 }}
              >
                {t('retryTranslation')}
              </Button>
            )}
          </Stack>
        ) : translationState.status === 'no-translation' ? (
          /* ── No translation available ────────────────────────────────── */
          <Stack spacing={1.5} alignItems="flex-start">
            <Alert severity="info" data-testid="translation-unavailable" sx={{ py: 0.5, width: '100%' }}>
              {t('translationUnavailable')}
            </Alert>
            <Stack direction="row" spacing={1}>
              {editHook && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleWriteFromScratch}
                  data-testid="translation-write-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('writeTranslation')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={onGetTranslation}
                data-testid="get-translation-btn"
                sx={{ minHeight: 44 }}
              >
                {t('getTranslation')}
              </Button>
            </Stack>
          </Stack>
        ) : translationState.status === 'quota-exceeded' ? (
          /* ── Quota-exceeded state — distinct from generic error ───────── */
          <Stack spacing={1.5} alignItems="flex-start" data-testid="translation-quota-exceeded">
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('translationQuotaExceeded')}
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                component="a"
                href={ROUTES.SUBSCRIPTION}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quota-upgrade-btn"
                sx={{ minHeight: 44 }}
              >
                {t('upgradeSubscription')}
              </Button>
              {onGetTranslation && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onGetTranslation}
                  data-testid="translation-retry-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('retryTranslation')}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : translationState.status === 'error' ? (
          /* ── Error ───────────────────────────────────────────────────── */
          <Stack spacing={1.5} alignItems="flex-start">
            <Alert
              severity="error"
              data-testid="translation-error"
              sx={{ py: 0.5, width: '100%' }}
            >
              {t('translationFetchError', { message: translationState.message })}
            </Alert>
            <Stack direction="row" spacing={1}>
              {editHook && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleWriteFromScratch}
                  data-testid="translation-write-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('writeTranslation')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={onGetTranslation}
                data-testid="get-translation-btn"
                sx={{ minHeight: 44 }}
              >
                {t('getTranslation')}
              </Button>
            </Stack>
          </Stack>
        ) : (
          /* ── Idle: placeholder + Write Translation (primary) + Get Translation ── */
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography variant="body1" color="text.secondary">
              {t('translationPlaceholder')}
            </Typography>
            <Stack direction="row" spacing={1}>
              {editHook && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleWriteFromScratch}
                  data-testid="translation-write-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('writeTranslation')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={onGetTranslation}
                data-testid="get-translation-btn"
                sx={{ minHeight: 44 }}
              >
                {t('getTranslation')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
      {segment && (
        <NoteModal
          open={isNoteModalOpen}
          onClose={() => setIsNoteModalOpen(false)}
          segmentId={segment.id}
          noteType="translation"
          sourceText={noteSourceText}
          hasExistingNote={segment.hasTranslationNote ?? false}
        />
      )}
    </Box>
  );
};
